import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Download,
  AlertCircle,
  CheckCircle,
  Ticket
} from 'lucide-react';
import { GAM_TICKET_LOGO } from '@/config/assets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicTicketPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${ticketId}`);
      setTicket(response.data);
    } catch (err) {
      setError('Ticket not found');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        `${API_URL}/api/tickets/${ticketId}/pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket_${ticketId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  const isValid = (status) => {
    const normalizedStatus = status?.toLowerCase();
    return normalizedStatus === 'unused' || normalizedStatus === 'valid';
  };

  const isUsed = (status) => {
    return status?.toLowerCase() === 'used';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ticket...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Ticket Not Found</h2>
            <p className="text-gray-500 mb-6">
              The ticket you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => navigate('/')} variant="outline">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#0055FF] shadow-lg mx-auto mb-2">
          <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
        </div>
        <h1 className="text-xl font-bold text-gray-800">Your Digital Ticket</h1>
      </div>

      {/* Clean Ticket Card with Blue Border */}
      <div className="max-w-sm mx-auto">
        <div className="p-3 bg-[#0055FF] rounded-2xl shadow-xl">
          <div className="bg-white rounded-xl overflow-hidden">
            {/* Status Banner */}
            {isUsed(ticket.status) && (
              <div className="bg-gray-500 text-white py-3 px-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-mono font-bold tracking-[0.3em] text-sm">TICKET USED</span>
                </div>
              </div>
            )}

            {isValid(ticket.status) && (
              <div className="bg-green-500 text-white py-3 px-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-mono font-bold tracking-[0.3em] text-sm">VALID TICKET</span>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="bg-gray-100 py-4 px-6 border-b-2 border-gray-800">
              <p className="font-mono text-gray-400 text-xs tracking-[0.2em] text-center">================================</p>
              <h3 className="font-mono text-xl font-bold text-gray-800 text-center my-2 tracking-wider">GAM TICKET</h3>
              <p className="font-mono text-gray-400 text-xs tracking-[0.2em] text-center">================================</p>
            </div>

            {/* Event Details */}
            <div className="p-6 border-b border-gray-200">
              <div className="space-y-4">
                <div>
                  <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] mb-1">EVENT:</p>
                  <p className="font-mono text-lg font-semibold text-gray-900">{ticket.event_name}</p>
                </div>
                <div>
                  <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] mb-1">DATE:</p>
                  <p className="font-mono text-lg text-gray-900">{ticket.date || 'TBA'}</p>
                </div>
                <div>
                  <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] mb-1">TIME:</p>
                  <p className="font-mono text-lg text-gray-900">{ticket.time || 'TBA'}</p>
                </div>
                <div>
                  <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] mb-1">VENUE:</p>
                  <p className="font-mono text-lg text-gray-900">{ticket.venue || 'TBA'}</p>
                </div>
              </div>
            </div>

            {/* Ticket ID - Dashed border */}
            <div className="py-4 px-6 border-b-2 border-dashed border-gray-300">
              <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] text-center mb-1">TICKET ID:</p>
              <p className="font-mono text-xl font-bold text-gray-900 text-center tracking-wider">{ticket.ticket_id}</p>
            </div>

            {/* QR Code - Large and clear - Uses simple ticket_id for easy scanning */}
            <div className="p-6 bg-white flex justify-center">
              <div className={`p-4 border-2 border-dashed border-gray-300 rounded-lg bg-white ${isUsed(ticket.status) ? 'opacity-70' : ''}`}>
                <QRCode 
                  value={ticket.ticket_id}
                  size={220}
                  level="L"
                  fgColor="#000000"
                  bgColor="#FFFFFF"
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                />
              </div>
            </div>

            {/* Footer message */}
            <div className="py-3 px-6 bg-gray-50 border-t border-gray-200">
              {isValid(ticket.status) && (
                <p className="text-xs text-green-600 text-center flex items-center justify-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Show this QR code at the venue entrance
                </p>
              )}
              {isUsed(ticket.status) && (
                <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  This ticket has already been used
                </p>
              )}
            </div>

            {/* Price */}
            <div className="py-4 px-6 bg-gray-50 border-t border-gray-100">
              <div className="text-center">
                <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] mb-1">PRICE PAID:</p>
                <p className="text-2xl font-bold text-[#0055FF]">D{ticket.price}</p>
              </div>
            </div>

            {/* Powered by */}
            <div className="py-3 px-6 bg-gray-100 border-t border-gray-200">
              <div className="flex items-center justify-center gap-2">
                <Ticket className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500">Powered by <span className="font-bold text-gray-700">Gam Ticket</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex justify-center gap-3">
        <Button
          onClick={downloadPDF}
          disabled={downloading}
          variant="outline"
          className="bg-white"
        >
          {downloading ? (
            <div className="w-4 h-4 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mr-2" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Download PDF
        </Button>
      </div>

      {/* Instructions */}
      <div className="mt-8 text-center max-w-sm mx-auto">
        <p className="text-gray-500 text-sm">
          Present this QR code at the venue entrance for scanning. 
          Screenshot this page to save your ticket.
        </p>
      </div>
    </div>
  );
}
